/**
 * 易联云文本打印指令工具
 * 规范：58mm=32字节/行，72mm=48字节/行；汉字2字节，ASCII 1字节（近似 GBK）
 * @see https://doc2.10ss.net/#/md/指令文档说明
 */

export const PAPER_BYTES = {
  58: 32,
  72: 48,
};

export const FONT_TAGS = ["normal", "FB", "FH", "FW", "FS", "FS2"];

/** 与后台 orderprint 占位符一致（含店铺资料 / 会员卡） */
export const TEMPLATE_VARS = [
  "shop_name",
  "shop_address",
  "shop_phone",
  "ordertime",
  "orderid",
  "tableid",
  "paytype",
  "goodslist",
  "cardno",
  "member_name",
  "member_username",
  "member_phone",
  "member_amount",
  "member_hex",
];

export const SAMPLE_ITEMS = [
  { name: "精酿啤酒", qty: "500ML", price: "38.00" },
];

/** 预览用示例商品区（对应后台 {goodslist} 替换结果） */
export function buildSampleGoodslist(items = SAMPLE_ITEMS) {
  const rows = (items || []).map(
    (r) =>
      `<table><tr><td>${r.name || ""}</td><td>${r.qty || ""}</td><td>${r.price || ""}</td></tr></table>`
  );
  const total = (items || []).reduce((sum, r) => {
    const n = parseFloat(String(r.price || "").replace(/[^\d.]/g, ""));
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  rows.push(
    `<table><tr><td>总计金额</td><td></td><td>${total.toFixed(2)}</td></tr></table>`
  );
  return rows.join("");
}

export const SAMPLE_DATA = {
  shop_name: "示例门店",
  shop_address: "示例路 88 号",
  shop_phone: "13800138000",
  ordertime: "2026-08-01 14:30:00",
  orderid: "202608011430001",
  tableid: "A01",
  paytype: "现金支付",
  goodslist: buildSampleGoodslist(),
  // 会员卡（订单关联 uid 时由后台填充；预览用示例）
  cardno: "8600123456789012",
  member_cardno: "8600123456789012",
  member_name: "张三",
  member_username: "zhangsan",
  member_phone: "13800138000",
  member_amount: "128.50",
  member_hex: "A1B2C3D4",
  // 兼容旧占位 / 文案
  address: "示例路 88 号",
  phone: "13800138000",
  tel: "13800138000",
  order_id: "202608011430001",
  order_time: "2026-08-01 14:30:00",
  pay_amount: "38.00",
  customer_name: "张**",
  customer_phone: "138****8000",
  remark: "",
};

let _uid = 0;
export function uid(prefix = "b") {
  _uid += 1;
  return `${prefix}_${Date.now().toString(36)}_${_uid}`;
}

/** 近似 GBK 字节宽度：非 ASCII 按 2 字节 */
export function byteWidth(str) {
  if (str == null || str === "") return 0;
  let n = 0;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    n += s.charCodeAt(i) > 0x7f ? 2 : 1;
  }
  return n;
}

export function lineBytes(paperWidth) {
  return PAPER_BYTES[paperWidth] || PAPER_BYTES[58];
}

/** 居中前置空格数（按文档：(纸宽-自身宽度)/2） */
export function centerPadCount(text, paperWidth) {
  const max = lineBytes(paperWidth);
  const w = byteWidth(text);
  if (w >= max) return 0;
  return Math.floor((max - w) / 2);
}

export function centerWithSpaces(text, paperWidth) {
  const pad = centerPadCount(text, paperWidth);
  return `${" ".repeat(pad)}${text}`;
}

export function makeSeparator(paperWidth, char = "-") {
  const max = lineBytes(paperWidth);
  const c = char && char.length ? char[0] : "-";
  return c.repeat(max);
}

export function replaceVars(text, data = SAMPLE_DATA) {
  if (text == null) return "";
  let s = String(text);
  // 预览时去掉会员块包裹标记（后台无 uid 时会整段删除）
  s = s.replace(/\{#member\}\s*/g, "").replace(/\s*\{\/#member\}/g, "");
  // {{key}} 与后台 {key} 均支持
  s = s.replace(/\{\{(\w+)\}\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(data, key) ? String(data[key]) : `{{${key}}}`
  );
  s = s.replace(/\{(\w+)\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(data, key) ? String(data[key]) : `{${key}}`
  );
  // 与 orderprint 一致的固定文案替换（预览用）
  const shopName = data.shop_name != null ? data.shop_name : "";
  const shopAddress =
    data.shop_address != null
      ? data.shop_address
      : data.address != null
        ? data.address
        : "";
  const shopPhone =
    data.shop_phone != null
      ? data.shop_phone
      : data.phone != null
        ? data.phone
        : data.tel != null
          ? data.tel
          : "";
  if (shopName !== "") s = s.split("店名").join(String(shopName));
  if (shopAddress !== "") s = s.split("测试地址").join(String(shopAddress));
  if (shopPhone !== "") s = s.split("测试电话").join(String(shopPhone));
  return s;
}

function wrapFont(inner, font) {
  if (!font || font === "normal") return inner;
  return `<${font}>${inner}</${font}>`;
}

/** 与现网 yilianyun.txt 一致，优先使用 CA/RA */
function wrapAlign(inner, align) {
  if (align === "center") return `<CA>${inner}</CA>`;
  if (align === "right") return `<RA>${inner}</RA>`;
  return inner;
}

function ensureNewline(s) {
  if (!s) return "\n";
  return s.endsWith("\n") ? s : `${s}\n`;
}

export function createBlock(type, overrides = {}) {
  const base = {
    id: uid(),
    type,
    text: "",
    left: "",
    right: "",
    align: "left",
    font: "normal",
    char: "-",
    value: "{orderid}",
    barcodeType: "BR2",
    rows: SAMPLE_ITEMS.map((r) => ({ ...r })),
    // 店铺信息
    showName: true,
    showAddress: true,
    showPhone: true,
    nameFont: "FS2",
    nameAlign: "center",
    contactFont: "FB",
    nameText: "{shop_name}",
    addressPrefix: "地址：",
    addressText: "{shop_address}",
    phonePrefix: "电话：",
    phoneText: "{shop_phone}",
    // 支付方式
    payLayout: "table",
    payLabel: "支付方式",
    payValue: "{paytype}",
    // 会员卡（订单 uid > 0 时打印）
    showCardno: true,
    showMemberName: false,
    showMemberUsername: false,
    showMemberPhone: false,
    showMemberAmount: false,
    showMemberHex: false,
    memberLayout: "text",
    cardnoLabel: "会员卡号",
    memberNameLabel: "会员姓名",
    memberUsernameLabel: "会员账号",
    memberPhoneLabel: "会员手机",
    memberAmountLabel: "账户余额",
    memberHexLabel: "卡ID",
  };

  switch (type) {
    case "title":
      Object.assign(base, {
        text: "{shop_name}",
        align: "center",
        font: "FS2",
      });
      break;
    case "text":
      Object.assign(base, { text: "", align: "left", font: "normal" });
      break;
    case "separator":
      Object.assign(base, { char: "-" });
      break;
    case "blank":
      break;
    case "lr":
      Object.assign(base, { left: "支付方式", right: "{paytype}" });
      break;
    case "table":
      Object.assign(base, {
        font: "normal",
        rows: SAMPLE_ITEMS.map((r) => ({ ...r })),
      });
      break;
    case "qr":
      Object.assign(base, { value: "{orderid}" });
      break;
    case "barcode":
      Object.assign(base, { value: "{orderid}", barcodeType: "BR2" });
      break;
    case "shopinfo":
      Object.assign(base, {
        showName: true,
        showAddress: true,
        showPhone: true,
        nameFont: "FS2",
        nameAlign: "center",
        contactFont: "FB",
        nameText: "{shop_name}",
        addressPrefix: "地址：",
        addressText: "{shop_address}",
        phonePrefix: "电话：",
        phoneText: "{shop_phone}",
      });
      break;
    case "payway":
      Object.assign(base, {
        payLayout: "table",
        payLabel: "支付方式",
        payValue: "{paytype}",
        font: "normal",
      });
      break;
    case "member":
      Object.assign(base, {
        showCardno: true,
        showMemberName: false,
        showMemberUsername: false,
        showMemberPhone: false,
        showMemberAmount: false,
        showMemberHex: false,
        memberLayout: "text",
        font: "FB",
        cardnoLabel: "会员卡号",
        memberNameLabel: "会员姓名",
        memberUsernameLabel: "会员账号",
        memberPhoneLabel: "会员手机",
        memberAmountLabel: "账户余额",
        memberHexLabel: "卡ID",
      });
      break;
    default:
      break;
  }

  return { ...base, ...overrides, id: overrides.id || base.id, type };
}

/** 单块 → 易联云指令片段 */
export function blockToContent(block, paperWidth = 58) {
  if (!block) return "";
  switch (block.type) {
    case "blank":
      return "\n";
    case "separator":
      return ensureNewline(makeSeparator(paperWidth, block.char || "-"));
    case "lr":
      return ensureNewline(`<LR>${block.left || ""},${block.right || ""}</LR>`);
    case "table": {
      const rows = Array.isArray(block.rows) ? block.rows : [];
      const trs = rows
        .map((r) => {
          const name = (r && r.name) || "";
          const qty = (r && r.qty) || "";
          const price = (r && r.price) || "";
          return `<tr><td>${name}</td><td>${qty}</td><td>${price}</td></tr>`;
        })
        .join("");
      let inner = `<table>${trs}</table>`;
      inner = wrapFont(inner, block.font);
      return ensureNewline(inner);
    }
    case "qr":
      return ensureNewline(`<QR>${block.value || ""}</QR>`);
    case "barcode": {
      const tag = block.barcodeType === "BR" ? "BR" : "BR2";
      return ensureNewline(`<${tag}>${block.value || ""}</${tag}>`);
    }
    case "shopinfo": {
      let out = "";
      // 仅 showName 为真时输出店名，避免未显式关闭时打出空 <CA></CA>
      if (block.showName) {
        let nameLine = block.nameText || "{shop_name}";
        nameLine = wrapAlign(nameLine, block.nameAlign || "center");
        nameLine = wrapFont(nameLine, block.nameFont || "FS2");
        out += ensureNewline(nameLine);
      }
      if (block.showAddress) {
        const line = `${block.addressPrefix || "地址："}${
          block.addressText || "{shop_address}"
        }`;
        out += ensureNewline(wrapFont(line, block.contactFont || "FB"));
      }
      if (block.showPhone) {
        const line = `${block.phonePrefix || "电话："}${
          block.phoneText || "{shop_phone}"
        }`;
        out += ensureNewline(wrapFont(line, block.contactFont || "FB"));
      }
      return out || "\n";
    }
    case "payway": {
      const label = block.payLabel || "支付方式";
      const value = block.payValue || "{paytype}";
      const layout = block.payLayout || "table";
      if (layout === "lr") {
        return ensureNewline(`<LR>${label},${value}</LR>`);
      }
      if (layout === "text") {
        let inner = `${label}:${value}`;
        inner = wrapFont(inner, block.font);
        return ensureNewline(inner);
      }
      // 与现网 yilianyun.txt 一致的两列表格
      let inner = `<table><tr><td>${label}</td><td>${value}</td></tr></table>`;
      inner = wrapFont(inner, block.font);
      return ensureNewline(inner);
    }
    case "member": {
      // 用 {#member}...{/#member} 包裹，无关联 uid 时后台整段删除
      const rows = [];
      if (block.showCardno) {
        rows.push([block.cardnoLabel || "会员卡号", "{cardno}"]);
      }
      if (block.showMemberName) {
        rows.push([block.memberNameLabel || "会员姓名", "{member_name}"]);
      }
      if (block.showMemberUsername) {
        rows.push([block.memberUsernameLabel || "会员账号", "{member_username}"]);
      }
      if (block.showMemberPhone) {
        rows.push([block.memberPhoneLabel || "会员手机", "{member_phone}"]);
      }
      if (block.showMemberAmount) {
        rows.push([block.memberAmountLabel || "账户余额", "{member_amount}"]);
      }
      if (block.showMemberHex) {
        rows.push([block.memberHexLabel || "卡ID", "{member_hex}"]);
      }
      if (!rows.length) {
        rows.push([block.cardnoLabel || "会员卡号", "{cardno}"]);
      }
      const layout = block.memberLayout || "text";
      let out = "";
      for (let i = 0; i < rows.length; i++) {
        const label = rows[i][0];
        const value = rows[i][1];
        if (layout === "lr") {
          out += ensureNewline(`<LR>${label},${value}</LR>`);
        } else if (layout === "table") {
          let inner = `<table><tr><td>${label}</td><td>${value}</td></tr></table>`;
          inner = wrapFont(inner, block.font || "FB");
          out += ensureNewline(inner);
        } else {
          let inner = `${label}:${value}`;
          inner = wrapFont(inner, block.font || "FB");
          out += ensureNewline(inner);
        }
      }
      if (!out) return "\n";
      return `{#member}\n${out}{/#member}\n`;
    }
    case "title":
    case "text":
    default: {
      let inner = block.text || "";
      inner = wrapAlign(inner, block.align);
      inner = wrapFont(inner, block.font);
      return ensureNewline(inner);
    }
  }
}

/** 块列表 → 完整指令 content */
export function generateContent(blocks, options = {}) {
  const paperWidth = options.paperWidth || 58;
  const copies = Math.min(9, Math.max(1, Number(options.copies) || 1));
  let out = "";
  if (copies > 1) {
    out += `<MN>${copies}</MN>`;
  }
  const list = Array.isArray(blocks) ? blocks : [];
  for (let i = 0; i < list.length; i++) {
    out += blockToContent(list[i], paperWidth);
  }
  return out;
}

/**
 * 按字节宽度折行（预览用）
 */
export function wrapByBytes(text, maxBytes) {
  const s = String(text || "");
  if (!s) return [""];
  const lines = [];
  let cur = "";
  let curW = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    const w = ch.charCodeAt(0) > 0x7f ? 2 : 1;
    if (curW + w > maxBytes && cur) {
      lines.push(cur);
      cur = ch;
      curW = w;
    } else {
      cur += ch;
      curW += w;
    }
  }
  if (cur || !lines.length) lines.push(cur);
  return lines;
}

/** 将指令解析为预览行 */
export function contentToPreviewLines(content, paperWidth = 58, data = SAMPLE_DATA) {
  return rebuildPreviewFromContent(content, paperWidth, data);
}

function tagStyle(tag) {
  const t = String(tag || "").toUpperCase();
  const style = {};
  if (t === "CENTER" || t === "CA") style.center = true;
  if (t === "RIGHT" || t === "RA") style.right = true;
  if (t === "FB") style.bold = true;
  if (t === "FS2" || t === "FS3" || t === "FS4" || t === "FS5" || t === "FS6" || t === "FS7") {
    style.large = true;
    style.fs2 = true;
  } else if (t === "FS" || /^FH/.test(t) || /^FW/.test(t)) {
    style.large = true;
  }
  return style;
}

function mergeStyle(base, extra) {
  return {
    bold: !!(base && base.bold) || !!(extra && extra.bold),
    large: !!(base && base.large) || !!(extra && extra.large),
    fs2: !!(base && base.fs2) || !!(extra && extra.fs2),
    center: !!(base && base.center) || !!(extra && extra.center),
    right: !!(base && base.right) || !!(extra && extra.right),
  };
}

function hasStructuralInner(inner) {
  return /<(table|QR2?|BR\d?|LR2?|center|right|CA|RA|FB|FH2?|FW2?|FS2?|FS[3-7])[\s>]/i.test(
    inner || ""
  );
}

/** 更稳定的预览：顺序扫描 content */
function rebuildPreviewFromContent(content, paperWidth, data, baseStyle = {}) {
  const max = lineBytes(paperWidth);
  let raw = replaceVars(content || "", data);
  raw = raw.replace(/<MN2?>\d+<\/MN2?>/gi, "");
  raw = raw.replace(/<RS>\d+<\/RS>/gi, "");
  raw = raw.replace(/<ML>\d<\/ML>/gi, "");
  raw = raw.replace(/<MP>\d<\/MP>/gi, "");
  raw = raw.replace(/<MK2?>\d<\/MK2?>/gi, "");
  raw = raw.replace(/<PW>\d+<\/PW>/gi, "");
  raw = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  const lines = [];
  const emitText = (text, style = {}) => {
    const st = mergeStyle(baseStyle, style);
    let t = text.replace(/\\t/g, "    ");
    if (st.center && t) t = centerWithSpaces(t, paperWidth);
    else if (st.right && t) {
      const pad = Math.max(0, max - byteWidth(t));
      t = `${" ".repeat(pad)}${t}`;
    }
    wrapByBytes(t, max).forEach((w) => {
      lines.push({
        type: "text",
        text: w,
        bold: !!st.bold,
        large: !!st.large,
        fs2: !!st.fs2,
        center: !!st.center,
        right: !!st.right,
      });
    });
  };

  let i = 0;
  const s = raw;
  const openTag = /^<([A-Za-z][A-Za-z0-9]*)>/;
  let atLineStart = true;

  const markEmitted = () => {
    atLineStart = false;
  };

  while (i < s.length) {
    if (s[i] === "\n" || (s[i] === "\\" && s[i + 1] === "n")) {
      if (s[i] === "\\") i += 2;
      else i += 1;
      if (atLineStart) {
        lines.push({ type: "text", text: "" });
      }
      atLineStart = true;
      continue;
    }

    if (s[i] === "<") {
      const rest = s.slice(i);
      const mTable = rest.match(/^<table>([\s\S]*?)<\/table>/i);
      if (mTable) {
        const body = mTable[1];
        const trRe = /<tr>([\s\S]*?)<\/tr>/gi;
        let tm;
        let rowIndex = 0;
        while ((tm = trRe.exec(body))) {
          if (rowIndex > 0) {
            // 行与行之间换行
            atLineStart = true;
          }
          const cells = [];
          const tdRe = /<td>([\s\S]*?)<\/td>/gi;
          let dm;
          while ((dm = tdRe.exec(tm[1]))) {
            cells.push(dm[1].replace(/<[^>]+>/g, ""));
          }
          const name = cells[0] || "";
          const qty = cells[1] || "";
          const price = cells[2] || "";
          const spaces = Math.max(1, max - byteWidth(name) - byteWidth(qty) - byteWidth(price));
          const gap1 = Math.floor(spaces / 2);
          const gap2 = spaces - gap1;
          emitText(name + " ".repeat(gap1) + qty + " ".repeat(gap2) + price, { large: true });
          markEmitted();
          rowIndex += 1;
        }
        i += mTable[0].length;
        continue;
      }

      const mQr = rest.match(/^<QR2?>([\s\S]*?)<\/QR2?>/i);
      if (mQr) {
        lines.push({ type: "qr", text: "[QR]" });
        markEmitted();
        i += mQr[0].length;
        continue;
      }

      const mBr = rest.match(/^<BR\d?>([\s\S]*?)<\/BR\d?>/i);
      if (mBr) {
        lines.push({ type: "barcode", text: "||||||||||||||||" });
        markEmitted();
        i += mBr[0].length;
        continue;
      }

      const mLr = rest.match(/^<LR2?>([^,]*),([^<]*)<\/LR2?>/i);
      if (mLr) {
        const l = mLr[1] || "";
        const r = mLr[2] || "";
        const spaces = Math.max(1, max - byteWidth(l) - byteWidth(r));
        emitText(`${l}${" ".repeat(spaces)}${r}`);
        markEmitted();
        i += mLr[0].length;
        continue;
      }

      const mWrap = rest.match(
        /^<(center|right|CA|RA|FB|FH2?|FW2?|FS2?|FS[3-7]|FI)>([\s\S]*?)<\/\1>/i
      );
      if (mWrap) {
        const style = tagStyle(mWrap[1]);
        const inner = mWrap[2];
        if (hasStructuralInner(inner)) {
          const nested = rebuildPreviewFromContent(inner, paperWidth, data, mergeStyle(baseStyle, style));
          nested.forEach((line) => lines.push(line));
          if (nested.length) markEmitted();
        } else {
          emitText(inner.replace(/<[^>]+>/g, ""), style);
          markEmitted();
        }
        i += mWrap[0].length;
        continue;
      }

      const om = rest.match(openTag);
      if (om) {
        i += om[0].length;
        continue;
      }
      if (rest.startsWith("</")) {
        const cm = rest.match(/^<\/[A-Za-z][A-Za-z0-9]*>/);
        if (cm) {
          i += cm[0].length;
          continue;
        }
      }
    }

    let j = i;
    while (j < s.length && s[j] !== "<" && s[j] !== "\n" && !(s[j] === "\\" && s[j + 1] === "n")) {
      j++;
    }
    if (j > i) {
      emitText(s.slice(i, j));
      markEmitted();
      i = j;
    } else {
      i += 1;
    }
  }

  while (lines.length && lines[lines.length - 1].type === "text" && lines[lines.length - 1].text === "") {
    lines.pop();
  }
  return lines;
}

/** 从块直接预览（更准确） */
export function blocksToPreviewLines(blocks, paperWidth = 58, data = SAMPLE_DATA) {
  const content = generateContent(blocks, { paperWidth, copies: 1 });
  return contentToPreviewLines(content, paperWidth, data);
}

/**
 * 默认模板：对齐现网 ./template/yilianyun.txt（orderprint 使用）
 * 店铺字段使用 {shop_name}/{shop_address}/{shop_phone}，后台同时兼容旧文案
 */
export function defaultBlocks() {
  return [
    createBlock("shopinfo", {
      showName: true,
      showAddress: false,
      showPhone: false,
      nameFont: "FS2",
      nameAlign: "center",
    }),
    createBlock("text", { text: "订单时间:{ordertime}", font: "FB" }),
    createBlock("text", { text: "订单编号:{orderid}", font: "FB" }),
    createBlock("text", { text: "桌号:{tableid}", font: "FB" }),
    createBlock("text", { text: "------------------------------" }),
    createBlock("table", {
      font: "normal",
      rows: [{ name: "商品名称", qty: "酒量", price: "价格" }],
    }),
    createBlock("text", { text: "{goodslist}", font: "normal" }),
    createBlock("payway", { payLayout: "table", font: "normal" }),
    createBlock("text", { text: "------------------------------" }),
    createBlock("shopinfo", {
      showName: false,
      showAddress: true,
      showPhone: true,
      contactFont: "FB",
    }),
  ];
}

export function presetMeituan() {
  return [
    createBlock("text", { text: "顾客联", font: "FS2" }),
    createBlock("title", { text: "**#2 美团**", align: "center", font: "FS2" }),
    createBlock("text", { text: "在线支付", align: "center" }),
    createBlock("text", { text: "{shop_name}", align: "center" }),
    createBlock("separator", { char: "*" }),
    createBlock("text", { text: "预计送达时间: 立即送达" }),
    createBlock("text", { text: "下单时间: {ordertime}" }),
    createBlock("text", { text: "订单号: {orderid}" }),
    createBlock("separator", { char: "-" }),
    createBlock("text", { text: "**************商品**************", align: "center" }),
    createBlock("table", { font: "normal", rows: SAMPLE_ITEMS.map((r) => ({ ...r })) }),
    createBlock("separator", { char: "-" }),
    createBlock("lr", { left: "配送费", right: "0.0" }),
    createBlock("text", { text: "实际支付: {paytype}", font: "FS2" }),
    createBlock("separator", { char: "-" }),
    createBlock("text", { text: "地址：{shop_address}" }),
    createBlock("text", { text: "电话：{shop_phone}" }),
    createBlock("separator", { char: "*" }),
    createBlock("text", { text: "-- 完 --", align: "center" }),
  ];
}

/** 外卖风格备选（非默认） */
export function presetFlashBuy() {
  return [
    createBlock("shopinfo", {
      showName: true,
      showAddress: false,
      showPhone: false,
      nameFont: "FS2",
      nameAlign: "center",
    }),
    createBlock("text", { text: "--在线支付--", align: "center", font: "FS" }),
    createBlock("separator", { char: "*" }),
    createBlock("text", { text: "订单时间:{ordertime}", font: "FB" }),
    createBlock("text", { text: "订单编号:{orderid}", font: "FB" }),
    createBlock("separator", { char: "-" }),
    createBlock("table", {
      font: "FS",
      rows: SAMPLE_ITEMS.map((r) => ({ ...r })),
    }),
    createBlock("separator", { char: "-" }),
    createBlock("payway", { payLayout: "text", font: "FS" }),
    createBlock("separator", { char: "*" }),
    createBlock("shopinfo", {
      showName: false,
      showAddress: true,
      showPhone: true,
      contactFont: "FB",
    }),
    createBlock("text", { text: "-- 完 --", align: "center", font: "FS2" }),
  ];
}

export function normalizeBlocks(list) {
  if (!Array.isArray(list) || !list.length) return defaultBlocks();
  return list.map((b) => {
    const type = (b && b.type) || "text";
    return createBlock(type, { ...b, id: (b && b.id) || uid() });
  });
}
